import {
  type FC, memo, useEffect, useMemo,
} from 'react';
import { type GroupRadiusProximity } from '~/_shared/types/proximity/proximity.types';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { ActiveProximityHighlightContainer } from '~/map/map/proximity/activeProximityHighlight.container';
import { HoveredProximityHighlightContainer } from '~/map/map/proximity/hoveredProximityHighlight.container';
import { useProximityStates } from '~/map/map/proximity/useProximityStates';
import { useGroupProximityZIndexes } from '~/map/zIndexes/useGroupProximityZIndex.hook';
import { type SpreadsheetLatLngRowData } from '~/store/selectors/spreadsheetDataMemoizedSelectors';
import { type SpreadsheetDataData } from '~/store/spreadsheetData/spreadsheetData.state';
import {
  type GroupProximityInput, type MapProximityManager,
} from './mapProximityManager';
import { type GroupRadiusProximityLocations } from './useProximityLocation';

type ProximityInstanceCircleGroupProps = {
  manager: MapProximityManager;
  proximity: GroupRadiusProximity;
  isVisible: boolean;
  spreadsheetData: SpreadsheetDataData;
  latLngData: SpreadsheetLatLngRowData;
  locations: GroupRadiusProximityLocations;
  zoom: number;
  circlesWithLabels: ReadonlyArray<{ spreadsheetRowId: SpreadsheetRowId }>;
};

const noLabels = [] as const;

export const ProximityInstanceCircleGroup: FC<ProximityInstanceCircleGroupProps> = memo(({
  circlesWithLabels,
  isVisible,
  locations,
  manager,
  proximity,
}) => {
  const hideLabel = useSelector(s => s.map.mapSettings.data.proximity.hideLabels);
  const { isActive, activeRowId, isHovered, hoveredRowId } = useProximityStates(proximity);

  const zIndexes = useGroupProximityZIndexes(() => ({
    rowIds: locations.map(loc => loc.spreadsheetRowId.rowId),
    proximityId: proximity.id,
  }), [locations, proximity.id]);

  const labeledCircles = hideLabel ? noLabels : circlesWithLabels;

  const data = useMemo(() => (
    locations.map((loc) => ({
      rowId: loc.spreadsheetRowId,
      latLng: loc.latLng,
      zIndex: zIndexes.get(loc.spreadsheetRowId.rowId)?.zIndex,
    })).filter((d): d is GroupProximityInput => !!d.zIndex)
  ), [locations, zIndexes]);

  useEffect(() => {
    manager.renderProximityGroupRadius(proximity, isVisible, data);
  }, [data, hideLabel, isVisible, locations, manager, proximity, zIndexes]);

  useEffect(() => {
    manager.renderProximityGroupRadiusLabels(proximity, isVisible, data, labeledCircles);
  }, [labeledCircles, data, isVisible, manager, proximity]);

  // Unmount cleanup
  useEffect(() => {
    return () => {
      manager.removeProximityCircle(proximity.id);
    };
  }, [manager, proximity.id]);

  const activeLocation = data.find(location => location.rowId.rowId === activeRowId?.rowId);
  const activeLocationCoordinates = activeLocation?.latLng;
  const hoveredLocation = data.find(location => location.rowId.rowId === hoveredRowId?.rowId);
  const hoveredLocationCoordinates = hoveredLocation?.latLng;

  return (
    <>
      {isActive && activeLocationCoordinates && (
        <ActiveProximityHighlightContainer
          rowId={activeLocation.rowId}
          coordinates={activeLocationCoordinates}
          proximity={proximity}
        />
      )}
      {isHovered && hoveredLocationCoordinates && (
        <HoveredProximityHighlightContainer
          rowId={hoveredLocation.rowId}
          coordinates={hoveredLocationCoordinates}
          proximity={proximity}
        />
      )}
    </>
  );
});
